import * as ngCore from '@angular/core'

import * as button from '../../button'

import * as fileDragAndDrop from './file-drag-and-drop'

@ngCore.Component({
  imports: [fileDragAndDrop.Directive, button.Component],
  selector: 'atn-ds-file',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** Indicates whether the Upload icon version should be displayed */
  public readonly isUploadIconSg = ngCore.input<boolean>(false, {
    alias: 'isUploadIcon',
  })

  /** Indicates whether the uploading file is in progress */
  public readonly isUploadingSg = ngCore.input<boolean>(false, {
    alias: 'isUploading',
  })
  /**
   * Reference to the file input element in the template. Used to access the
   * selected files from the native input element.
   */
  private readonly _inputRefSg =
    ngCore.viewChild<ngCore.ElementRef<HTMLInputElement>>('input')
  /**
   * Signal holding the pending file to be validated.
   * This is set directly by handlers and triggers validation effect.
   */
  private readonly _pendingFileSg = ngCore.signal<File | undefined>(undefined)
  /**
   * Effect that validates pending files and updates the file/error signals accordingly.
   * This runs whenever a new file is set via the pending signal.
   */
  private readonly _onPendingFileChange = ngCore.effect(() => {
    const file = this._pendingFileSg()
    const types = this.typesSg()

    // Validate file: returns File if valid, string (error) if invalid, undefined if no file
    const validationResult = !file
      ? undefined
      : !_isFileTypeValid(file, types)
        ? _getErrorMessage(file, types)
        : file

    // Apply side effects based on validation result
    if (typeof validationResult === 'string') {
      // Error: file was rejected
      this.errorSg.set(validationResult)
      this.fileSg.set(undefined)
      // Clear input on error
      const inputElement = this._inputRefSg()?.nativeElement
      if (inputElement) {
        inputElement.value = ''
      }
    } else {
      // Valid: File or undefined
      this.errorSg.set(undefined)
      this.fileSg.set(validationResult)
    }
  })
  /** Signal holding the currently validated and accepted file. */
  public readonly fileSg = ngCore.signal<File | undefined>(undefined)
  /**
   * Effect that emits file changes whenever the validated file signal updates.
   */
  private readonly _onFileChange = ngCore.effect(() => {
    this.fileChange.emit(this.fileSg())
  })
  /** Signal holding the error message when a file is rejected. */
  public readonly errorSg = ngCore.signal<string | undefined>(undefined)
  /** Emits when a file is selected or changed. */
  public readonly fileChange = ngCore.output<File | undefined>()
  /** Accepted file types (MIME types or file extensions). */
  public typesSg = ngCore.input<string[]>([], { alias: 'types' })
  /**
   * Handles file selection changes. Retrieves the selected file from the input
   * element and sets it to the pending signal for validation.
   */
  public fileChangeHandler() {
    const inputElement = this._inputRefSg()?.nativeElement
    if (inputElement) {
      const file = inputElement.files?.[0]
      this._pendingFileSg.set(file)
      // Clear the input value to allow re-selection of the same file if needed
      inputElement.value = ''
    }
  }
  /**
   * Handles files dropped via drag and drop. Extracts the first file from the
   * FileList and sets it to the pending signal for validation.
   */
  public fileDroppedHandler(files: FileList) {
    const file = files?.[0]
    this._pendingFileSg.set(file)
  }
  /**
   * Clears the current error message.
   */
  public clearError() {
    this.errorSg.set(undefined)
  }
}

/**
 * Validates if a file matches the accepted types. Supports MIME types (with
 * wildcards like "image/*"), file extensions (with or without dot), and
 * accepts all files when no types are specified.
 */
function _isFileTypeValid(file: File, acceptedTypes: string[]): boolean {
  // If no types specified, accept everything
  if (acceptedTypes.length === 0) {
    return true
  }

  const fileType = file.type
  const fileName = file.name.toLowerCase()

  return acceptedTypes.some(acceptedType => {
    const normalizedType = acceptedType.toLowerCase().trim()

    // Handle MIME type matching (e.g., "image/png")
    if (normalizedType.includes('/')) {
      // Support wildcards like "image/*"
      if (normalizedType.endsWith('/*')) {
        const category = normalizedType.slice(0, -2)
        return fileType.startsWith(category + '/')
      }
      return fileType === normalizedType
    }

    // Handle extension matching (e.g., ".pdf" or "pdf")
    const extension = normalizedType.startsWith('.')
      ? normalizedType
      : '.' + normalizedType
    return fileName.endsWith(extension)
  })
}

/**
 * Generates a user-friendly error message with HTML formatting for rejected
 * files, including the file name and list of accepted types.
 */
function _getErrorMessage(file: File, acceptedTypes: string[]): string {
  if (acceptedTypes.length === 0) {
    return `<div class="atn-ds-file__error-message">File <b>"${file.name}"</b> could not be processed.</div>`
  }
  const typesList = acceptedTypes.join(', ')
  return `
    <div class="atn-ds-file__error-content">
      <div class="atn-ds-file__error-message">File <b>"${file.name}"</b> is not accepted.</div>
      <div class="atn-ds-file__error-label">Accepted types: <span class="atn-ds-file__error-types">${typesList}</span></div>
    </div>
  `
}
